import { ArticleCard } from '@dealstack/frontend/app/pages/HelpBlog/ArticleCard/ArticleCard.tsx';
import { AuthorPageContainer } from '@dealstack/frontend/app/pages/HelpBlog/AuthorPage/AuthorPage.styles.tsx';
import { HelpBlogLayout } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlogLayout/HelpBlogLayout.tsx';
import { useArticles } from '@dealstack/frontend/app/pages/HelpBlog/api/useArticles.ts';
import { useAuthor } from '@dealstack/frontend/app/pages/HelpBlog/api/useAuthor.ts';
import { makeDynamicHelpBlogUrl } from '@dealstack/frontend/app/pages/HelpBlog/help-blog.utils.ts';
import { useHelpBlogBreadcrumbs } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogBreadcrumbs.tsx';
import { HelpBlogContext } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogController.tsx';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav.tsx';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader.tsx';
import {
	useCurrentLanguage,
	useTranslationHelpBlog,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { Flex, Text, Title } from '@mantine/core';
import { useContext, useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ArticleCardProps } from '@dealstack/frontend/app/pages/HelpBlog/ArticleCard/ArticleCard.types';

export const AuthorPage = () => {
	const { dataset, perspective, mediaResolver } = useContext(HelpBlogContext);
	const tHelpBlog = useTranslationHelpBlog();
	const currentLanguage = useCurrentLanguage();
	const navigate = useNavigate();

	const { slug, dealId } = useParams();
	const { author, isFetching, error } = useAuthor(dataset, slug, perspective);
	const { articles } = useArticles(
		dataset,
		{
			enabled: !!author?.id,
			refId: author?.id || '',
			language: currentLanguage,
		},
		perspective
	);

	useHelpBlogBreadcrumbs([
		{
			name: tHelpBlog('breadcrumbs.help_crumb'),
			url: ROUTES.HELP_BLOG,
		},
		{
			name: tHelpBlog('breadcrumbs.help.authors'),
			url: `${ROUTES.HELP_BLOG}/${HELP_BLOG_ROUTES.AUTHORS}`,
		},
		{
			name: author?.name || '',
			url: ROUTES.HELP_BLOG,
			noLink: true,
		},
	]);

	const articlesCount = articles?.length || 0;
	const createdAtDate = formatStringDateToUI(author?.createdAt, DATE_FORMATS.DATE_MONTH_YEAR);
	const metaInfoText = tHelpBlog('authorPage.metaInfo');
	const authorMetaInfo = `${articlesCount} ${metaInfoText} ${createdAtDate}`;
	const image = author?.image;
	const imageUrl = image
		? mediaResolver(image).width(100).height(100).auto('format').url()
		: undefined;

	const articleCards = useMemo<ArticleCardProps[]>(() => {
		return articles?.map((article) => {
			const publishedAt = formatStringDateToUI(
				article.createdAt,
				DATE_FORMATS.DATE_MONTH_YEAR
			);
			const readTime = article.estimatedReadingTimeMinutes || 1;
			const readTimeText = tHelpBlog('article.card.readTime');
			const metaContent = `${publishedAt} • ${readTime} ${readTimeText}`;
			const articleUrl = makeDynamicHelpBlogUrl(
				dealId,
				`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.ARTICLE, {
					slug: article.slug.current,
				})}`
			);
			const categoryUrl = makeDynamicHelpBlogUrl(
				dealId,
				`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.CATEGORY, {
					slug: article.category.slug.current,
				})}`
			);

			return {
				title: article.title,
				articleUrl,
				imageUrl: article?.image
					? mediaResolver(article.image).width(400).height(200).auto('format').url()
					: undefined,
				imageAlt: article.title,
				categoryName: article.category.name,
				categoryUrl,
				metaContent,
				previewText: article.previewSummary,
				mediaResolver,
			};
		});
	}, [articles, tHelpBlog, dealId]);

	if (isFetching) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (error || !author) {
		navigate(ROUTES.PAGE_NOT_FOUND);
		return null;
	}

	return (
		<HelpBlogLayout title={author.name}>
			<AuthorPageContainer>
				<div className="container">
					<div className="breadcrumbs">
						<QuickSwitchBreadcrumbsNav showQuickSwitch={false} />
					</div>

					<div className="header">
						{imageUrl && (
							<div className="image">
								<img src={imageUrl} alt={author.name} />
							</div>
						)}

						<Title size="h1" fw={600} className="title">
							{author.name}
						</Title>

						<Flex gap="0.625rem" align="center" className="meta">
							<Text fz="lg" fw={600} className="subtitle-text">
								{author.position}
							</Text>

							<span className="separator"></span>

							<Text className="meta-text">{authorMetaInfo}</Text>
						</Flex>
					</div>

					<Flex className="articles" wrap="wrap" gap="2rem">
						{articleCards?.map((article, idx) => {
							return (
								<div key={articles[idx].id} className="card">
									<ArticleCard
										title={article.title}
										articleUrl={article.articleUrl}
										categoryName={article.categoryName}
										categoryUrl={article.categoryUrl}
										metaContent={article.metaContent}
										previewText={article.previewText}
										mediaResolver={article.mediaResolver}
									/>
								</div>
							);
						})}
					</Flex>
				</div>
			</AuthorPageContainer>
		</HelpBlogLayout>
	);
};
